import { AlertTriangle, Boxes, CheckCircle2, ShieldAlert } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { NumberInput } from "@/components/ui/number-input";

import type { EditMinStockFormValues } from "./stocks-schema";
import type { StockItem } from "./types";
import type { UseFormReturn } from "react-hook-form";

interface EditMinStockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: StockItem | null;
  form: UseFormReturn<EditMinStockFormValues>;
  isSubmitting: boolean;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  setPreset: (qty: number) => void;
  adjustPreset: (delta: number) => void;
}

export function EditMinStockDialog({
  open,
  onOpenChange,
  item,
  form,
  isSubmitting,
  onSubmit,
  setPreset,
  adjustPreset,
}: EditMinStockDialogProps) {
  if (!item) return null;

  const currentMin = form.watch("minStock") ?? 0;
  const willBeLowStock =
    item.currentStock > 0 && item.currentStock <= currentMin;
  const willBeSafe = item.currentStock > currentMin;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Boxes className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold">
                Edit Batas Minimum Stok
              </DialogTitle>
              <DialogDescription className="text-xs">
                Atur batas peringatan restock untuk barang persediaan ini.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Item Info Card */}
        <div className="rounded-lg border bg-muted/30 p-3.5 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="font-semibold text-sm text-foreground">
                {item.name}
              </h4>
              <p className="text-xs text-muted-foreground">
                {item.sku ? `SKU: ${item.sku} • ` : ""}
                {item.categoryName}
              </p>
            </div>
            <Badge
              variant="outline"
              className={
                item.type === "raw_material"
                  ? "border-blue-200 bg-blue-50 text-blue-700 text-[11px]"
                  : "border-purple-200 bg-purple-50 text-purple-700 text-[11px]"
              }
            >
              {item.type === "raw_material" ? "Bahan Baku" : "Produk Siap Jual"}
            </Badge>
          </div>

          <div className="flex items-center justify-between pt-1 border-t text-xs">
            <span className="text-muted-foreground">Stok Fisik Saat Ini:</span>
            <span className="font-semibold text-foreground">
              {item.currentStock.toLocaleString("id-ID")} {item.uomName}
            </span>
          </div>
        </div>

        <Form {...form}>
          <form
            id="edit-min-stock-form"
            onSubmit={onSubmit}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="minStock"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel className="text-xs font-semibold">
                      Batas Minimum Stok ({item.uomName})
                    </FormLabel>
                    <span className="text-[11px] text-muted-foreground">
                      Sebelumnya: {item.minStock.toLocaleString("id-ID")}
                    </span>
                  </div>
                  <FormControl>
                    <div className="relative">
                      <NumberInput
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="0"
                        className="pr-14 text-base font-semibold"
                        autoFocus
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                        {item.uomName}
                      </span>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Quick Presets and Increments */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>Tombol Cepat:</span>
                <span>Tambah / Kurang:</span>
              </div>
              <div className="flex items-center justify-between gap-1.5 flex-wrap">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {[2, 5, 10, 20].map((preset) => (
                    <Button
                      key={preset}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 sm:h-7 px-2.5 text-xs"
                      onClick={() => setPreset(preset)}
                    >
                      {preset}
                    </Button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {[-5, -1, 1, 5].map((delta) => (
                    <Button
                      key={delta}
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="h-8 sm:h-7 px-2.5 text-xs font-mono"
                      onClick={() => adjustPreset(delta)}
                    >
                      {delta > 0 ? `+${delta}` : delta}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Status Prediction Badge */}
            <div className="rounded-md p-2.5 bg-muted/40 border text-xs flex items-center gap-2">
              {willBeSafe ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span className="text-muted-foreground text-[11px]">
                    Dengan batas ini, status stok akan tetap{" "}
                    <strong className="text-emerald-600">Aman</strong>.
                  </span>
                </>
              ) : willBeLowStock ? (
                <>
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                  <span className="text-muted-foreground text-[11px]">
                    Dengan batas ini, status stok akan ditandai{" "}
                    <strong className="text-amber-600">Menipis</strong> (perlu
                    restock).
                  </span>
                </>
              ) : (
                <>
                  <ShieldAlert className="h-4 w-4 text-rose-600 shrink-0" />
                  <span className="text-muted-foreground text-[11px]">
                    Stok fisik saat ini kosong (
                    <strong className="text-rose-600">Habis</strong>).
                  </span>
                </>
              )}
            </div>
          </form>
        </Form>

        <DialogFooter className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="min-h-[40px] w-full sm:w-auto"
          >
            Batal
          </Button>
          <Button
            type="submit"
            form="edit-min-stock-form"
            disabled={isSubmitting}
            className="min-h-[40px] w-full sm:w-auto"
          >
            {isSubmitting ? "Menyimpan..." : "Simpan Batas Minimum"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
